import type { ContextSnapshot } from '../models/flock-telemetry.models';
import type { SeatWorkQueue } from '../models/work-queue.model';
import { parseUtcMs } from '../models/flock-telemetry.utils';
import { seatsByAccount } from './pace.logic';

/**
 * How recently a seat must have worked to count under its account (Tim,
 * 2026-09-29, Bug 34817): idle longer than this, or absent from telemetry,
 * and it is not one of that account's seats.
 */
export const SEAT_ACTIVE_WINDOW_MS = 24 * 60 * 60_000;

/** The context-snapshot fields the activity rule reads. */
export type SeatActivity = Pick<
  ContextSnapshot,
  'instanceId' | 'lastActivityAt'
>;

/**
 * True when `lastActivityAt` is within {@link SEAT_ACTIVE_WINDOW_MS} of now.
 * `lastActivityAt` is the publisher's "now minus the seat's transcript idle
 * time", the only stamp that moves with real work; `observedAt` stays fresh
 * for an idle seat on a live host, so it is deliberately not a fallback.
 * Missing or unparseable means inactive.
 */
export function isSeatActive(
  lastActivityAt: string | null | undefined,
  nowMs: number
): boolean {
  const ms = parseUtcMs(lastActivityAt ?? null);
  return ms !== null && nowMs - ms <= SEAT_ACTIVE_WINDOW_MS;
}

/**
 * Seats whose context snapshot is active, joined by instance id (any case).
 * `snapshots === null` means context has not loaded yet: nothing is removed,
 * so a first paint or a failed context read never blanks every account.
 */
export function filterActiveSeats<T extends { instanceId: string }>(
  seats: readonly T[],
  snapshots: readonly SeatActivity[] | null,
  nowMs: number
): T[] {
  if (snapshots === null) return [...seats];
  const active = new Set(
    snapshots
      .filter((s) => isSeatActive(s.lastActivityAt, nowMs))
      .map((s) => s.instanceId.toLowerCase())
  );
  return seats.filter((s) => active.has(s.instanceId.toLowerCase()));
}

/** The wallboard store reads {@link activeSeatsByAccount} needs. */
export interface SeatActivitySource {
  seats(): readonly SeatWorkQueue[] | null;
  contextSnapshots(): readonly SeatActivity[] | null;
  now(): number;
}

/**
 * {@link seatsByAccount} over active seats only, so account alerts ("N seats
 * will stall") count the same seats the Accounts page lists.
 */
export function activeSeatsByAccount(
  data: SeatActivitySource
): Map<string, string[]> {
  return seatsByAccount(
    filterActiveSeats(data.seats() ?? [], data.contextSnapshots(), data.now())
  );
}
